import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { StyledTextField } from '@modules/ui-components';
import type { AdCreativeAssetReference, AdCreativeItem } from '@segmently/schema-models/ad-creative';
import {
  AD_CREATIVE_MANUAL_WEBVTT_MAX_BYTES,
  type AdCreativeManualVideoTranscriptImportRequest,
  type AdCreativeTimedTranscriptView,
  type AdCreativeVideoTranscriptLibraryView,
  type AdCreativeVideoTranscriptTaskView,
  type AdCreativeVideoTranscriptVersionView,
} from '../../services/AdCreativeLocalizationApiContract';
import type { AdCreativeApiClient } from '../../services/AdCreativeApiClient';
import { AD_CREATIVE } from '../../selectors';
import { exactSourceVideoAssetWithGeometry } from './videoLocalizationSource';

type VideoTranscriptClient = Pick<
  AdCreativeApiClient,
  | 'listVideoTranscripts'
  | 'getVideoTranscript'
  | 'createVideoTranscript'
  | 'getTask'
  | 'cancelTask'
  | 'importManualVideoTranscript'
  | 'selectVideoTranscript'
>;

export interface VideoTranscriptPanelProps {
  projectId: string;
  item: AdCreativeItem;
  slotKey: string;
  client: VideoTranscriptClient;
  automaticTranscriptionModel: string;
  disabled?: boolean;
  onCurrentTranscriptChange?: () => void;
}

type ReviewerAttestationDraft = AdCreativeManualVideoTranscriptImportRequest['reviewerAttestation'];

const EMPTY_ATTESTATION: ReviewerAttestationDraft = {
  textReviewed: false,
  timingReviewed: false,
  rightsConfirmed: false,
};

const TRANSCRIPT_TASK_POLL_ATTEMPTS = 30;
const TRANSCRIPT_TASK_POLL_INTERVAL_MS = 1_000;

export function VideoTranscriptPanel({
  projectId,
  item,
  slotKey,
  client,
  automaticTranscriptionModel,
  disabled = false,
  onCurrentTranscriptChange,
}: VideoTranscriptPanelProps): React.ReactElement {
  const [library, setLibrary] = useState<AdCreativeVideoTranscriptLibraryView | null>(null);
  const [reviewedVersion, setReviewedVersion] = useState<AdCreativeVideoTranscriptVersionView | null>(null);
  const [reviewedTranscript, setReviewedTranscript] = useState<AdCreativeTimedTranscriptView | null>(null);
  const [webVtt, setWebVtt] = useState('');
  const [selectedFileName, setSelectedFileName] = useState('');
  const [attestation, setAttestation] = useState<ReviewerAttestationDraft>(EMPTY_ATTESTATION);
  const [selectImportedAsCurrent, setSelectImportedAsCurrent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mutationBusy, setMutationBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [activeTranscriptTask, setActiveTranscriptTask] = useState<AdCreativeVideoTranscriptTaskView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const identityRef = useRef('');

  const identity = `${projectId}:${item.id}:${item.sourceRevision}:${slotKey}`;
  identityRef.current = identity;
  const sourceAsset = useMemo(
    () => exactSourceVideoAssetWithGeometry(item, slotKey),
    [item, slotKey],
  );

  const exactRequest = useMemo(() => ({
    projectId,
    itemId: item.id,
    slotKey,
    sourceRevision: item.sourceRevision,
  }), [item.id, item.sourceRevision, projectId, slotKey]);

  const loadLibrary = useCallback(async (
    expectedIdentity = identity,
    commit = true,
  ): Promise<AdCreativeVideoTranscriptLibraryView> => {
    if (!sourceAsset) throw new Error('This slot has no exact trusted source-locale video asset.');
    const response = await client.listVideoTranscripts({
      ...exactRequest,
      model: automaticTranscriptionModel,
    });
    if (!response.success || !response.data?.library) {
      throw new Error(response.error || 'Timed transcript library could not be loaded.');
    }
    const nextLibrary = response.data.library;
    assertExactTranscriptLibrary(nextLibrary, item, slotKey, sourceAsset, automaticTranscriptionModel);
    if (commit && identityRef.current === expectedIdentity) setLibrary(nextLibrary);
    return nextLibrary;
  }, [automaticTranscriptionModel, client, exactRequest, identity, item, slotKey, sourceAsset]);

  useEffect(() => {
    let active = true;
    setLibrary(null);
    setReviewedVersion(null);
    setReviewedTranscript(null);
    setWebVtt('');
    setSelectedFileName('');
    setAttestation(EMPTY_ATTESTATION);
    setSelectImportedAsCurrent(false);
    setError(null);
    setStatus(null);
    setActiveTranscriptTask(null);
    setCancelBusy(false);
    setLoading(true);
    void loadLibrary(identity)
      .catch((nextError) => {
        if (active && identityRef.current === identity) {
          setError(errorMessage(nextError));
        }
      })
      .finally(() => {
        if (active && identityRef.current === identity) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [identity, loadLibrary]);

  const reviewVersion = async (version: AdCreativeVideoTranscriptVersionView): Promise<void> => {
    const expectedIdentity = identity;
    setMutationBusy(true);
    setError(null);
    try {
      const response = await client.getVideoTranscript({ ...exactRequest, evidenceId: version.evidenceId });
      if (!response.success || !response.data?.version || !response.data.transcript) {
        throw new Error(response.error || 'Timed transcript version could not be loaded.');
      }
      if (!sourceAsset) throw new Error('This slot has no exact trusted source-locale video asset.');
      if (!library?.sourceComponentHash) throw new Error('Exact video component lineage is unavailable.');
      assertExactTranscriptVersion(
        response.data.version,
        item,
        slotKey,
        version.evidenceId,
        sourceAsset,
        library.sourceComponentHash,
      );
      assertExactTimedTranscript(response.data.transcript, response.data.version);
      if (identityRef.current !== expectedIdentity) return;
      setReviewedVersion(response.data.version);
      setReviewedTranscript(response.data.transcript);
      setStatus(`Loaded immutable transcript version ${response.data.version.evidenceId}.`);
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setMutationBusy(false);
    }
  };

  const selectVersion = async (version: AdCreativeVideoTranscriptVersionView): Promise<void> => {
    const expectedIdentity = identity;
    setMutationBusy(true);
    setError(null);
    setStatus(null);
    try {
      const response = await client.selectVideoTranscript({ ...exactRequest, evidenceId: version.evidenceId });
      if (!response.success || response.data?.currentEvidenceId !== version.evidenceId) {
        throw new Error(response.error || 'Timed transcript version could not be selected.');
      }
      await loadLibrary(expectedIdentity);
      if (identityRef.current !== expectedIdentity) return;
      setStatus('This immutable transcript version is now selected for caption localization.');
      onCurrentTranscriptChange?.();
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setMutationBusy(false);
    }
  };

  const importManualTranscript = async (): Promise<void> => {
    const expectedIdentity = identity;
    setMutationBusy(true);
    setError(null);
    setStatus(null);
    try {
      if (!sourceAsset) throw new Error('This slot has no exact trusted source-locale video asset.');
      const response = await client.importManualVideoTranscript({
        ...exactRequest,
        webVtt,
        reviewerAttestation: attestation,
        selectAsCurrent: selectImportedAsCurrent,
      });
      if (!response.success || !response.data?.evidenceId || !response.data.transcript) {
        throw new Error(response.error || 'Manual timed transcript could not be imported.');
      }
      const nextLibrary = await loadLibrary(expectedIdentity);
      if (identityRef.current !== expectedIdentity) return;
      const importedVersion = nextLibrary.versions.find((version) => version.evidenceId === response.data?.evidenceId);
      if (!importedVersion) throw new Error('Imported transcript version was not returned by the exact-source library.');
      assertExactTranscriptVersion(
        importedVersion,
        item,
        slotKey,
        response.data.evidenceId,
        sourceAsset,
        nextLibrary.sourceComponentHash,
      );
      assertExactTimedTranscript(response.data.transcript, importedVersion);
      setReviewedVersion(importedVersion);
      setReviewedTranscript(response.data.transcript);
      setWebVtt('');
      setSelectedFileName('');
      setAttestation(EMPTY_ATTESTATION);
      setSelectImportedAsCurrent(false);
      setStatus(selectImportedAsCurrent
        ? 'Manual timed transcript imported and selected for caption localization.'
        : 'Manual timed transcript imported as a new immutable version. Select it when it is ready for caption localization.');
      if (selectImportedAsCurrent) onCurrentTranscriptChange?.();
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setMutationBusy(false);
    }
  };

  const observeAutomaticTranscriptTask = async (
    initialTask: AdCreativeVideoTranscriptTaskView,
    expectedIdentity: string,
  ): Promise<void> => {
    if (!library || !sourceAsset) {
      throw new Error('The exact video transcript library is unavailable.');
    }
    let task = initialTask;
    assertExactAutomaticTranscriptTask(task, exactRequest, library, automaticTranscriptionModel);
    setActiveTranscriptTask(task);

    for (let attempt = 0; attempt < TRANSCRIPT_TASK_POLL_ATTEMPTS; attempt += 1) {
      if (identityRef.current !== expectedIdentity) return;
      setStatus(formatTranscriptTaskProgress(task));
      if (isTranscriptTaskTerminal(task.status)) break;
      if (isTranscriptTaskDeliveryRetryable(task)) break;

      const response = await client.getTask(projectId, task.id);
      if (!response.success || !response.data?.task) {
        throw new Error(response.error || 'Automatic transcript progress could not be loaded.');
      }
      task = normalizeAutomaticTranscriptTask(response.data.task);
      assertExactAutomaticTranscriptTask(task, exactRequest, library, automaticTranscriptionModel, initialTask.id);
      if (identityRef.current !== expectedIdentity) return;
      setActiveTranscriptTask(task);
      setStatus(formatTranscriptTaskProgress(task));
      if (isTranscriptTaskTerminal(task.status)) break;
      if (isTranscriptTaskDeliveryRetryable(task)) break;
      if (attempt + 1 < TRANSCRIPT_TASK_POLL_ATTEMPTS) {
        await transcriptPollDelay(TRANSCRIPT_TASK_POLL_INTERVAL_MS);
      }
    }

    if (identityRef.current !== expectedIdentity) return;
    if (task.status === 'cancelled') {
      setActiveTranscriptTask(null);
      setStatus('Automatic timed transcription was cancelled. No transcript version was selected.');
      return;
    }
    if (task.status === 'error') {
      setActiveTranscriptTask(null);
      throw new Error(task.error?.message || 'Automatic timed transcription failed safely.');
    }
    if (task.status !== 'completed') {
      setActiveTranscriptTask(task);
      setStatus(`${formatTranscriptTaskProgress(task)} It is still running in the background; check progress again here.`);
      return;
    }
    if (!task.result?.evidenceId || task.result.selected !== false) {
      throw new Error('Completed automatic transcription did not return one unselected immutable evidence version.');
    }

    const nextLibrary = await loadLibrary(expectedIdentity, false);
    if (identityRef.current !== expectedIdentity) return;
    const generatedVersion = nextLibrary.versions.find(
      (version) => version.evidenceId === task.result?.evidenceId,
    );
    if (!generatedVersion) {
      throw new Error('Generated transcript version was not returned by the exact-source library.');
    }
    assertExactTranscriptVersion(
      generatedVersion,
      item,
      slotKey,
      task.result.evidenceId,
      sourceAsset,
      nextLibrary.sourceComponentHash,
    );
    if (generatedVersion.selected || nextLibrary.currentEvidenceId === generatedVersion.evidenceId) {
      throw new Error('Automatic transcript creation must not select the generated version. Review it before selecting it separately.');
    }
    const transcriptResponse = await client.getVideoTranscript({
      ...exactRequest,
      evidenceId: task.result.evidenceId,
    });
    if (!transcriptResponse.success
      || !transcriptResponse.data?.version
      || !transcriptResponse.data.transcript) {
      throw new Error(transcriptResponse.error || 'Generated transcript version could not be loaded for review.');
    }
    assertExactTranscriptVersion(
      transcriptResponse.data.version,
      item,
      slotKey,
      task.result.evidenceId,
      sourceAsset,
      nextLibrary.sourceComponentHash,
    );
    assertExactTimedTranscript(transcriptResponse.data.transcript, transcriptResponse.data.version);
    if (transcriptResponse.data.version.selected) {
      throw new Error('Automatic transcript creation must not select the generated version. Review it before selecting it separately.');
    }
    setLibrary(nextLibrary);
    setReviewedVersion(transcriptResponse.data.version);
    setReviewedTranscript(transcriptResponse.data.transcript);
    setActiveTranscriptTask(null);
    setStatus('Automatic timed transcript generated as a new immutable version. Review it before selecting it for caption localization.');
  };

  const generateAutomaticTranscript = async (): Promise<void> => {
    const expectedIdentity = identity;
    setMutationBusy(true);
    setError(null);
    setStatus(null);
    try {
      if (!library?.automaticTranscription.supported) {
        throw new Error('Automatic timed transcription is unavailable for this exact video and model.');
      }
      if (!sourceAsset) throw new Error('This slot has no exact trusted source-locale video asset.');
      const response = await client.createVideoTranscript({
        ...exactRequest,
        model: automaticTranscriptionModel,
      });
      if (!response.success || !response.data?.task) {
        throw new Error(response.error || 'Automatic timed transcript could not be queued.');
      }
      await observeAutomaticTranscriptTask(response.data.task, expectedIdentity);
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setMutationBusy(false);
    }
  };

  const checkAutomaticTranscriptProgress = async (): Promise<void> => {
    if (!activeTranscriptTask) return;
    const expectedIdentity = identity;
    setMutationBusy(true);
    setError(null);
    try {
      await observeAutomaticTranscriptTask(activeTranscriptTask, expectedIdentity);
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setMutationBusy(false);
    }
  };

  const cancelAutomaticTranscript = async (): Promise<void> => {
    if (!activeTranscriptTask) return;
    const expectedIdentity = identity;
    setCancelBusy(true);
    setError(null);
    try {
      const response = await client.cancelTask(
        projectId,
        activeTranscriptTask.id,
        'User cancelled automatic video transcription',
      );
      if (!response.success) throw new Error(response.error || 'Automatic transcription cancellation failed.');
      if (identityRef.current !== expectedIdentity) return;
      setActiveTranscriptTask((current) => current && current.id === activeTranscriptTask.id
        ? { ...current, status: 'cancelling', phase: 'cancellation_queued' }
        : current);
      setStatus('Cancellation requested. The durable worker will stop at its next safe checkpoint.');
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setCancelBusy(false);
    }
  };

  const importReady = webVtt.trim().length > 0
    && byteLength(webVtt) <= AD_CREATIVE_MANUAL_WEBVTT_MAX_BYTES
    && attestation.textReviewed
    && attestation.timingReviewed
    && attestation.rightsConfirmed;

  return (
    <Box
      component="section"
      aria-labelledby="video-transcript-heading"
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, minWidth: 0 }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography id="video-transcript-heading" variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
            Source timed transcript
          </Typography>
          {library?.currentEvidenceId && <Chip size="small" color="success" variant="outlined" label="Selected for captions" />}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Import or reuse an immutable transcript for the exact current Segmently video. This never changes the source video or anything in Meta.
        </Typography>

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" role="status">
            <CircularProgress size={14} />
            <Typography variant="caption">Loading exact-source transcript versions…</Typography>
          </Stack>
        )}

        {library && (
          <Stack spacing={1}>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 0.75 }}>
              <Typography variant="caption" component="div">
                Source {library.sourceLocale} · {formatTranscriptDuration(library.durationMs)} · revision {library.sourceRevision}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ overflowWrap: 'anywhere' }}>
                Asset {library.sourceAssetId}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ overflowWrap: 'anywhere' }}>
                SHA-256 {library.sourceSha256}
              </Typography>
            </Box>

            {!library.automaticTranscription.supported ? (
              <Alert severity="info">
                Automatic timed transcription is unavailable. Import a reviewed WebVTT file; no provider is selected or billed.
              </Alert>
            ) : (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                <Stack spacing={0.75}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Automatic timed transcription</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${library.automaticTranscription.provider} · ${library.automaticTranscription.model} · ${library.automaticTranscription.features.join(', ') || 'timed cues'}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`Estimated tokens: ${library.automaticTranscription.estimatedInputTokens.toLocaleString()} input · ${library.automaticTranscription.estimatedOutputTokens.toLocaleString()} output`}
                  </Typography>
                  {activeTranscriptTask ? (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        data-testid={AD_CREATIVE.videoTranscriptCancel}
                        disabled={disabled
                          || cancelBusy
                          || (activeTranscriptTask.status === 'cancelling'
                            && !isTranscriptTaskCancellationRetryable(activeTranscriptTask))}
                        onClick={() => void cancelAutomaticTranscript()}
                      >
                        {cancelBusy
                          ? 'Cancelling…'
                          : isTranscriptTaskCancellationRetryable(activeTranscriptTask)
                            ? 'Retry cancellation'
                            : activeTranscriptTask.status === 'cancelling'
                              ? 'Cancelling…'
                          : 'Cancel transcription'}
                      </Button>
                      {!mutationBusy && (
                        <Button
                          size="small"
                          variant="contained"
                          data-testid={AD_CREATIVE.videoTranscriptCheckProgress}
                          disabled={disabled}
                          onClick={() => void (isTranscriptTaskDeliveryRetryable(activeTranscriptTask)
                            ? generateAutomaticTranscript()
                            : checkAutomaticTranscriptProgress())}
                        >
                          {isTranscriptTaskDeliveryRetryable(activeTranscriptTask)
                            ? 'Retry queue delivery'
                            : 'Check progress'}
                        </Button>
                      )}
                    </Stack>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      data-testid={AD_CREATIVE.videoTranscriptGenerate}
                      disabled={disabled || mutationBusy}
                      startIcon={mutationBusy ? <CircularProgress size={14} /> : undefined}
                      onClick={() => void generateAutomaticTranscript()}
                    >
                      {mutationBusy ? 'Queueing timed transcript…' : 'Generate timed transcript'}
                    </Button>
                  )}
                </Stack>
              </Box>
            )}

            <Stack spacing={0.75} aria-label="Timed transcript versions">
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Immutable versions ({library.versions.length})
              </Typography>
              {library.versions.length === 0 && (
                <Typography variant="caption" color="text.secondary">No timed transcript has been imported for this exact video.</Typography>
              )}
              {library.versions.map((version) => (
                <Box key={version.evidenceId} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 0.75 }}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {version.origin === 'manual_webvtt' ? 'Reviewed WebVTT' : 'Automatic transcript'}
                      </Typography>
                      {version.selected && <Chip size="small" color="success" label="Current" />}
                      <Chip size="small" variant="outlined" label={`${version.cueCount} cues`} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                      {version.evidenceId} · {formatTranscriptDate(version.createdAt)}
                    </Typography>
                    {version.reviewerAttestation && (
                      <Stack spacing={0.25}>
                        <Typography variant="caption" color="text.secondary">
                          Reviewed and attested by {version.reviewerAttestation.reviewerId} · text, timing, and rights confirmed
                        </Typography>
                        {version.reviewerAttestation.note && (
                          <Typography variant="caption" color="text.secondary">
                            Reviewer note: {version.reviewerAttestation.note}
                          </Typography>
                        )}
                      </Stack>
                    )}
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        disabled={disabled || mutationBusy}
                        onClick={() => void reviewVersion(version)}
                      >
                        Review cues
                      </Button>
                      {!version.selected && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={disabled || mutationBusy}
                          onClick={() => void selectVersion(version)}
                        >
                          Use for caption localization
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>

            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
              <Stack spacing={0.75}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Import reviewed WebVTT</Typography>
                <Button size="small" component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={disabled || mutationBusy}>
                  Choose .vtt file
                  <input
                    hidden
                    type="file"
                    accept=".vtt,text/vtt"
                    aria-label="WebVTT file"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = '';
                      if (!file) return;
                      if (file.size > AD_CREATIVE_MANUAL_WEBVTT_MAX_BYTES) {
                        setError(`WebVTT must be ${formatBytes(AD_CREATIVE_MANUAL_WEBVTT_MAX_BYTES)} or smaller.`);
                        return;
                      }
                      void file.text().then((value) => {
                        if (identityRef.current !== identity) return;
                        setWebVtt(value);
                        setSelectedFileName(file.name);
                        setError(null);
                      }).catch((nextError) => {
                        if (identityRef.current === identity) setError(errorMessage(nextError));
                      });
                    }}
                  />
                </Button>
                {selectedFileName && <Typography variant="caption">Selected file: {selectedFileName}</Typography>}
                <StyledTextField
                  fullWidth
                  multiline
                  minRows={5}
                  size="small"
                  label="Timed WebVTT"
                  helperText={`${formatBytes(byteLength(webVtt))} / ${formatBytes(AD_CREATIVE_MANUAL_WEBVTT_MAX_BYTES)}. Plain text cues only; the server verifies timing against the exact video duration.`}
                  value={webVtt}
                  disabled={disabled || mutationBusy}
                  onChange={(value) => {
                    setWebVtt(String(value));
                    setSelectedFileName('');
                  }}
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={attestation.textReviewed}
                      onChange={(event) => setAttestation((current) => ({ ...current, textReviewed: event.target.checked }))}
                    />
                  )}
                  label="I reviewed every cue against the exact source video"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={attestation.timingReviewed}
                      onChange={(event) => setAttestation((current) => ({ ...current, timingReviewed: event.target.checked }))}
                    />
                  )}
                  label="I reviewed cue timing and duration"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={attestation.rightsConfirmed}
                      onChange={(event) => setAttestation((current) => ({ ...current, rightsConfirmed: event.target.checked }))}
                    />
                  )}
                  label="I confirm we have rights to use this transcript"
                />
                <StyledTextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Reviewer note (optional)"
                  value={attestation.note ?? ''}
                  disabled={disabled || mutationBusy}
                  onChange={(value) => setAttestation((current) => ({ ...current, note: String(value) }))}
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectImportedAsCurrent}
                      onChange={(event) => setSelectImportedAsCurrent(event.target.checked)}
                    />
                  )}
                  label="Use this version for caption localization after import"
                />
                <Button
                  size="small"
                  variant="contained"
                  disabled={disabled || mutationBusy || !importReady}
                  onClick={() => void importManualTranscript()}
                >
                  {mutationBusy ? 'Working…' : 'Import immutable transcript'}
                </Button>
              </Stack>
            </Box>
          </Stack>
        )}

        {reviewedVersion && reviewedTranscript && (
          <Box component="section" aria-label="Timed transcript cue review" sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
            <Stack spacing={0.75}>
              <Typography variant="caption" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                Cue review · {reviewedVersion.evidenceId}
              </Typography>
              {reviewedTranscript.cues.map((cue) => (
                <Box key={cue.id} sx={{ borderInlineStart: 2, borderColor: 'divider', pl: 1 }}>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {formatTranscriptCueTime(cue.startMs)} → {formatTranscriptCueTime(cue.endMs)}
                  </Typography>
                  <Typography variant="body2" lang={reviewedTranscript.sourceLocale} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {cue.text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {error && <Alert severity="error" role="alert">{error}</Alert>}
        {status && <Alert severity={activeTranscriptTask ? 'info' : 'success'} role="status">{status}</Alert>}
      </Stack>
    </Box>
  );
}

export function formatTranscriptCueTime(valueMs: number): string {
  const safeMs = Number.isSafeInteger(valueMs) && valueMs >= 0 ? valueMs : 0;
  const hours = Math.floor(safeMs / 3_600_000);
  const minutes = Math.floor((safeMs % 3_600_000) / 60_000);
  const seconds = Math.floor((safeMs % 60_000) / 1_000);
  const milliseconds = safeMs % 1_000;
  return `${pad(hours, 2)}:${pad(minutes, 2)}:${pad(seconds, 2)}.${pad(milliseconds, 3)}`;
}

function formatTranscriptDuration(valueMs: number): string {
  if (!Number.isSafeInteger(valueMs) || valueMs <= 0) return 'unknown duration';
  return formatTranscriptCueTime(valueMs);
}

function formatTranscriptDate(value: string): string {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toLocaleString() : value;
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0 KB';
  return `${Math.ceil(value / 1024)} KB`;
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, '0');
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function assertExactTranscriptLibrary(
  library: AdCreativeVideoTranscriptLibraryView,
  item: AdCreativeItem,
  slotKey: string,
  sourceAsset: AdCreativeAssetReference,
  automaticTranscriptionModel: string,
): void {
  if (item.entityKind !== 'segmently_copy'
    || library.creativeItemId !== item.id
    || library.slotKey !== slotKey
    || library.sourceRevision !== item.sourceRevision
    || library.sourceLocale !== item.defaultLanguage
    || library.sourceAssetId !== sourceAsset.assetId
    || library.sourceSha256 !== sourceAsset.sha256
    || !/^[a-f0-9]{64}$/.test(library.sourceComponentHash)
    || library.durationMs !== sourceAsset.durationMs
    || !/^[a-f0-9]{64}$/.test(library.sourceSha256)
    || !Number.isSafeInteger(library.durationMs)
    || library.durationMs <= 0
    || !Array.isArray(library.versions)
    || library.versions.some((version) => (
      version.sourceAssetId !== library.sourceAssetId
      || version.sourceSha256 !== library.sourceSha256
      || version.sourceLocale !== library.sourceLocale
      || version.sourceComponentHash !== library.sourceComponentHash
      || typeof version.sourceRevision !== 'string'
      || version.sourceRevision.length === 0
      || version.slotKey !== library.slotKey
      || version.durationMs !== library.durationMs
      || version.verified !== true
    ))
    || library.versions.filter((version) => version.selected).length > 1
    || (library.automaticTranscription.supported && (
      library.automaticTranscription.provider !== 'google'
      || library.automaticTranscription.model !== automaticTranscriptionModel
      || !Array.isArray(library.automaticTranscription.features)
      || !Number.isSafeInteger(library.automaticTranscription.estimatedInputTokens)
      || !Number.isSafeInteger(library.automaticTranscription.estimatedOutputTokens)
      || library.automaticTranscription.estimatedInputTokens < 0
      || library.automaticTranscription.estimatedOutputTokens < 0
    ))
    || (library.currentEvidenceId !== null
      && !library.versions.some((version) => version.evidenceId === library.currentEvidenceId && version.selected))) {
    throw new Error('Transcript library returned mismatched exact-source lineage.');
  }
}

function assertExactTranscriptVersion(
  version: AdCreativeVideoTranscriptVersionView,
  item: AdCreativeItem,
  slotKey: string,
  evidenceId: string,
  sourceAsset: AdCreativeAssetReference,
  sourceComponentHash: string,
): void {
  if (version.evidenceId !== evidenceId
    || version.sourceComponentHash !== sourceComponentHash
    || !/^[a-f0-9]{64}$/.test(version.sourceComponentHash)
    || typeof version.sourceRevision !== 'string'
    || version.sourceRevision.length === 0
    || version.sourceLocale !== item.defaultLanguage
    || version.slotKey !== slotKey
    || version.verified !== true
    || version.sourceAssetId !== sourceAsset.assetId
    || version.sourceSha256 !== sourceAsset.sha256
    || version.durationMs !== sourceAsset.durationMs
    || !/^[a-f0-9]{64}$/.test(version.sourceSha256)
    || !Number.isSafeInteger(version.durationMs)
    || version.durationMs <= 0) {
    throw new Error('Transcript version returned mismatched exact-source lineage.');
  }
}

function normalizeAutomaticTranscriptTask(value: unknown): AdCreativeVideoTranscriptTaskView {
  const task = value as Partial<AdCreativeVideoTranscriptTaskView> | undefined;
  const statuses: AdCreativeVideoTranscriptTaskView['status'][] = [
    'pending',
    'running',
    'retrying',
    'cancelling',
    'completed',
    'error',
    'cancelled',
  ];
  if (!task
    || !/^adtranscribe_[a-f0-9]{40}$/.test(String(task.id || ''))
    || task.taskType !== 'ad-creative-video-transcription'
    || !statuses.includes(task.status as AdCreativeVideoTranscriptTaskView['status'])
    || typeof task.phase !== 'string'
    || !Number.isSafeInteger(task.progress)
    || Number(task.progress) < 0
    || Number(task.progress) > 100
    || typeof task.projectId !== 'string'
    || typeof task.creativeItemId !== 'string'
    || typeof task.slotKey !== 'string'
    || typeof task.sourceRevision !== 'string'
    || !/^[a-f0-9]{64}$/.test(String(task.sourceComponentHash || ''))
    || typeof task.provider !== 'string'
    || typeof task.model !== 'string') {
    throw new Error('Automatic transcript task returned invalid durable progress.');
  }
  return task as AdCreativeVideoTranscriptTaskView;
}

function assertExactAutomaticTranscriptTask(
  inputTask: AdCreativeVideoTranscriptTaskView,
  request: { projectId: string; itemId: string; slotKey: string; sourceRevision: string },
  library: AdCreativeVideoTranscriptLibraryView,
  model: string,
  expectedTaskId?: string,
): void {
  const task = normalizeAutomaticTranscriptTask(inputTask);
  if ((expectedTaskId && task.id !== expectedTaskId)
    || task.projectId !== request.projectId
    || task.creativeItemId !== request.itemId
    || task.slotKey !== request.slotKey
    || task.sourceRevision !== request.sourceRevision
    || task.sourceComponentHash !== library.sourceComponentHash
    || task.provider !== library.automaticTranscription.provider
    || task.model !== model
    || (task.result && (
      task.result.selected !== false
      || typeof task.result.evidenceId !== 'string'
      || task.result.evidenceId.length === 0
    ))) {
    throw new Error('Automatic transcript task returned mismatched exact-source lineage.');
  }
}

function isTranscriptTaskTerminal(status: AdCreativeVideoTranscriptTaskView['status']): boolean {
  return status === 'completed' || status === 'error' || status === 'cancelled';
}

function isTranscriptTaskDeliveryRetryable(task: AdCreativeVideoTranscriptTaskView): boolean {
  return task.status === 'retrying' && task.phase === 'enqueue_failed';
}

function isTranscriptTaskCancellationRetryable(task: AdCreativeVideoTranscriptTaskView): boolean {
  return task.status === 'cancelling' && task.phase === 'cancellation_enqueue_failed';
}

function formatTranscriptTaskProgress(task: AdCreativeVideoTranscriptTaskView): string {
  const phase = task.phase.replace(/_/g, ' ').trim() || 'working';
  return `Automatic timed transcription: ${phase} (${task.progress}%).`;
}

async function transcriptPollDelay(durationMs: number): Promise<void> {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, durationMs);
  });
}

function assertExactTimedTranscript(
  transcript: AdCreativeTimedTranscriptView,
  version: AdCreativeVideoTranscriptVersionView,
): void {
  if (transcript.artifactId !== version.evidenceId
    || transcript.sourceAssetId !== version.sourceAssetId
    || transcript.sourceSha256 !== version.sourceSha256
    || transcript.sourceLocale !== version.sourceLocale
    || transcript.durationMs !== version.durationMs
    || transcript.verified !== true
    || transcript.verifierEvidenceId !== version.verifierEvidenceId
    || !Array.isArray(transcript.cues)
    || transcript.cues.length !== version.cueCount
    || transcript.cues.some((cue, index) => (
      !Number.isSafeInteger(cue.startMs)
      || !Number.isSafeInteger(cue.endMs)
      || cue.startMs < 0
      || cue.endMs <= cue.startMs
      || cue.endMs > transcript.durationMs
      || !cue.text.trim()
      || (index > 0 && cue.startMs < transcript.cues[index - 1].endMs)
    ))) {
    throw new Error('Transcript cue response failed exact-source integrity checks.');
  }
}
